
import FileUpload from '../../../../components/stateful/fileUpload/FileUpload';
import RichTextEditor from '../../../../components/stateful/richTextEditor/RichTextEditor';
import { BlobContainer } from '../../../../utilities/enums/BlobContainer';
import { useDraftEditFormController } from './DraftEditFormController';
import './DraftEditForm.scss';
import TextField from '../../../../components/stateful/textField/TextField';
import CustomDialog from '../../../../components/stateless/dialog/CustomDialog';
import CustomButton from '../../../../components/stateless/customButton/CustomButton';
import SplitButton from '../../../../components/stateful/splitButton/SplitButton';
import { DraftNote } from '../../../../utilities/interfaces/DraftNote';
import HtmlRender from '../../../../components/stateless/htmlRender/HtmlRender';
import { NoteType } from '../../../../utilities/interfaces/NoteType';
import ExpansionPanel from '../../../../components/stateless/expansionPanel/ExpansionPanel';

interface DraftEditProps {
  draft: any;
}

/**
 *
 * @param draft Draft - accepts in a draft to populate the draft edit form
 * @returns React.Component
 */
const DraftEditForm = (props: DraftEditProps) => {
  const { draft } = props
  const {
    //variables
    baseUrl,
    // state
    chapterName,
    chapterNumber,
    chapterFiles,
    isHistoryOpen,
    guidelineDocumentName,
    noteTypeList,
    isNoteOpen,
    selectedNote,
    isPreviewOpen,
    previewHtml,
    isAgencyStatusAndRole,
    isSosStatusAndRole,
    // isGameAndParkTitle,
    // isSosRoleAndGameAndParkTitle,
    isValidStatus,
    // redux
    proposedRule,
    proposedRuleHistory,
    chapter,
    draftNotes,
    savingDraft,
    // functions
    saveForm,
    saveFile,
    deleteFile,
    handleChapterNameChange,
    handleChapterNumberChange,
    toggleHistory,
    downloadGuidelines,
    handleFileUploadResponseMessage,
    toggleNoteEditor,
    saveNote,
    openEditDraftNote,
    deleteNote,
    transformUpperCamelCase,
    togglePreview,
    createHTMLDocument,
  } = useDraftEditFormController(draft);

  return (
    <div>
      <div className='dialog-button'>
        <CustomButton onClick={downloadGuidelines} themeColor="success" icon="info">
          <a href={`${baseUrl}/fileStorage/GetAsByteArray/cms-items/${guidelineDocumentName}`} className="download-link">
            Formatting Guidelines
          </a>
        </CustomButton>
        <CustomButton onClick={toggleHistory}>View Edit History</CustomButton>
        <CustomButton onClick={togglePreview}>Preview</CustomButton>
        <i>Save draft to view most up-to-date preview</i>
      </div>
      <div className='draft-editor-container'>
        <CustomDialog open={isHistoryOpen} title={'Edit History'} toggleDialog={toggleHistory} hideButtons size="large">
          {proposedRuleHistory.map((editItem: any, index: number) => {
            return (
              <div key={index} className={`history-item`}>
                <div className='history-edit-label'>
                  <div><b>Edited on: {new Date(editItem.createdOn).toDateString()}</b></div>
                  { editItem.createdBy &&  editItem.createdBy !== null && editItem.createdBy !== '' && (
                    <div><b>Edited By: {editItem.createdBy}</b></div>
                  )}
                </div>
                {createHTMLDocument(editItem.changedTo)}
              </div>
            )
          })}
        </CustomDialog>
        <CustomDialog open={isNoteOpen} title={'Note Editor'} toggleDialog={toggleNoteEditor} hideButtons size="medium">
          <div className='note-editor'>
            <RichTextEditor
              content={selectedNote?.note ?? ''}
              onSave={saveNote}
              height='60vh'
            // height='50vh'
            />
          </div>
        </CustomDialog>
        <CustomDialog open={isPreviewOpen} title={'Preview'} toggleDialog={togglePreview} hideButtons size='large'>
          {previewHtml && (<HtmlRender value={previewHtml}></HtmlRender>)}
        </CustomDialog>
        <div>
          <div className='draft-actions'>
            <div className='draft-text-fields'>
              <TextField
                value={proposedRule?.proposedRuleName ?? chapterName ?? ''}
                title='Chapter Name'
                onChange={handleChapterNameChange}
              />
              <TextField
                value={proposedRule?.proposedRuleNumber ?? chapterNumber ?? ''}
                title='Chapter Number'
                onChange={handleChapterNumberChange}
              />
            </div>
          </div>
          {!isValidStatus && (
            <div style={{color: 'red'}}>
              This draft cannot be edited right now because it is associated to a filing that cannot be edited. This may be the case if:
              <ul>
                <li>The filing in question is not in the Agency inbox.</li>
                <li>The filing in question is in the Agency inbox and you do not have an Agency role.</li>
              </ul>
            </div>
          )}
          <div className="edit-tools">
            <div>
              {isAgencyStatusAndRole !== undefined && (
                <RichTextEditor
                  content={proposedRule?.proposedRuleHtml
                    ? proposedRule.proposedRuleHtml
                    : chapter?.chapterHtml
                      ? chapter.chapterHtml
                      : ''
                  }
                  height="60vh"
                  onSave={saveForm}
                  isSaving={savingDraft}
                  readOnly={!isValidStatus}
                />
              )}
              <div className='draft-note-editor'>
                <div className='horizontal-divider' />
                <h4>Notes</h4>
                {(isAgencyStatusAndRole || isSosStatusAndRole) && (
                  <div>
                    <b>Select to add a new note</b>
                    <div>
                      {noteTypeList && (
                        <div className="add-note-button">
                          <SplitButton options={noteTypeList} onClick={(event: any) => toggleNoteEditor(event)} defaultValue={'Footnote'} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {
                  draftNotes
                    ? (
                      draftNotes.map((noteItem: DraftNote, index: number) => {
                        let noteName = '';
                        noteTypeList.forEach((type: NoteType, index: number) => {
                          if (type.id === noteItem.noteTypeId) {
                            noteName = (transformUpperCamelCase(type.noteTypeName));
                          }
                        })
                        return (
                          <ExpansionPanel item={'test'} title={noteName} fullwidth>
                            <div>
                              <HtmlRender value={noteItem.note.toString()} className="draft-note-text" />
                              {/* <div className='horizontal-divider' /> */}
                              {(isAgencyStatusAndRole || isSosStatusAndRole) && (
                                <div className="button-bar">
                                  <CustomButton onClick={() => openEditDraftNote(noteItem)} themeColor='primary' fillMode='flat' icon='edit'>Edit Note</CustomButton>
                                  <CustomButton onClick={() => deleteNote(noteItem)} themeColor='error' fillMode='flat' icon='delete'>Delete</CustomButton>
                                </div>
                              )}
                            </div>
                          </ExpansionPanel>
                        );
                      })
                    )
                    : null
                }
              </div>
            </div>
            <div className="additional-content">
              <FileUpload
                containerName={BlobContainer.proposedRuleImageContainer}
                type='image'
                saveFile={saveFile}
                deleteFile={deleteFile}
                existingFiles={chapterFiles}
                notification={handleFileUploadResponseMessage}
                disabled={!isAgencyStatusAndRole}
              />
            </div>
            <div>

            </div>
          </div>
        </div>
      </div>
    </div >
  )
}

export default DraftEditForm